import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, Box, Modal, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { ActionModalContent } from './ActionModalContent';
import toast from 'react-hot-toast';

export type Item = 'post' | 'comment';
export interface ActionMenuProps {
  showBlockUser: boolean;
  onDelete: () => void;
  onBlock: () => void;
  item: Item;
  userEmail?: string;
  onReplyInDM?: () => void;
  showPinAction?: boolean;
  isPinned?: boolean;
  onPin?: () => void;
  pinLimitReached?: boolean;
  onGoToPost?: () => void;
}

export const ActionMenu = ({
  onReplyInDM,
  showBlockUser,
  onDelete,
  onBlock,
  item,
  userEmail,
  showPinAction,
  isPinned,
  onPin,
  pinLimitReached,
  onGoToPost,
}: ActionMenuProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [modalType, setModalType] = useState<'delete' | 'block'>('delete');
  const [isModalVisible, setModalVisible] = useState<boolean>(false);

  const openMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const deletePost = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete();
    setModalVisible(false);
  };
  const onDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setModalVisible(false);
  };

  const openModal = (
    event: React.MouseEvent<HTMLDivElement>,
    type: 'delete' | 'block',
  ) => {
    event.stopPropagation();
    closeMenu(event);
    setModalType(type);
    setModalVisible(true);
  };

  const handleBlock = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onBlock();
    setModalVisible(false);
  };

  return (
    <div data-testid="actionMenu">
      <Modal open={isModalVisible} onClose={onDismiss}>
        <>
          <ActionModalContent
            onSubmit={modalType === 'delete' ? deletePost : handleBlock}
            onDismiss={onDismiss}
            title={
              modalType === 'delete'
                ? `Are you sure you want to delete this ${item}?`
                : 'Are you sure you want to block this user?'
            }
            text={
              modalType === 'delete'
                ? undefined
                : 'This means that they will no longer be able to engage in your community.'
            }
            actionText={modalType === 'delete' ? 'Delete post' : 'Block user'}
          />
        </>
      </Modal>
      <div
        className="px-2.5 cursor-pointer hover:bg-[#6367ef0a]"
        onClick={openMenu}
        data-testid="actionMenuOpen"
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </div>
      <Popover
        open={!!menuAnchorEl}
        onClose={closeMenu}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
            },
          },
        }}
      >
        <Box
          className="flex flex-col min-w-[179px]"
          onClick={(e) => e.stopPropagation()}
        >
          {showPinAction && (
            <Box className="flex flex-col" data-testid="actionMenuPin">
              <Box className="flex gap-2 justify-between items-center w-full py-2 px-2 h-[40px]">
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                    color: '#000',
                  }}
                >
                  Pin to top
                </Typography>
                <Switch
                  checked={isPinned}
                  disabled={pinLimitReached && !isPinned}
                  onChange={(e) => {
                    e.stopPropagation();
                    onPin?.();
                    setMenuAnchorEl(null);
                  }}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5F1ECC',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#5F1ECC',
                    },
                  }}
                />
              </Box>
              {pinLimitReached && !isPinned && (
                <Typography className="text-[11px] text-gray-500 px-2 pb-2 max-w-[180px]">
                  Pin limit reached. Unpin a post to add a new one.
                </Typography>
              )}
            </Box>
          )}
          {onGoToPost && (
            <Box
              className="flex items-center cursor-pointer py-2 px-2 hover:bg-gray-50"
              onClick={(ev) => {
                ev.stopPropagation();
                onGoToPost();
                setMenuAnchorEl(null);
              }}
              data-testid="actionMenuGoToPost"
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#000',
                }}
              >
                Go to post
              </Typography>
            </Box>
          )}
          {userEmail && (
            <Box
              className="flex items-center cursor-pointer py-2 px-2 hover:bg-gray-50"
              onClick={(event) => {
                event.stopPropagation();
                navigator.clipboard.writeText(userEmail);
                closeMenu(event);
                toast.success('Email copied to clipboard!');
              }}
              data-testid="actionMenuCopyEmail"
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#000',
                }}
              >
                Copy email
              </Typography>
            </Box>
          )}
          {onReplyInDM && (
            <Box
              className="flex items-center cursor-pointer py-2 px-2 hover:bg-gray-50"
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchorEl(null);
                onReplyInDM();
              }}
              data-testid="actionMenuReplyInDM"
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#000',
                }}
              >
                Reply in DM
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          className="flex items-center cursor-pointer py-2 px-2 hover:bg-gray-50"
          onClick={(ev) => openModal(ev, 'delete')}
          data-testid="actionMenuDelete"
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#DA0000',
            }}
          >
            Delete {item}
          </Typography>
        </Box>
        {showBlockUser && (
          <Box
            className="flex items-center cursor-pointer py-2 px-2 hover:bg-gray-50"
            onClick={(ev) => openModal(ev, 'block')}
            data-testid="actionMenuBlock"
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#DA0000',
              }}
            >
              Block user
            </Typography>
          </Box>
        )}
      </Popover>
    </div>
  );
};
